import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import SignInMethods from "@/components/app/SignInMethods";
import DomainVerification from "@/components/app/DomainVerification";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const settings = user?.settings || {};
  const [position, setPosition] = useState(settings.widget_position || "bottom-right");
  const [size, setSize] = useState(settings.widget_size_preference || "small");
  const [hotkey, setHotkey] = useState(settings.hotkey || "Ctrl+Shift+Space");
  const [threshold, setThreshold] = useState(settings.distraction_threshold_seconds || 45);
  const [inactivity, setInactivity] = useState(settings.inactivity_threshold_seconds || 45);
  const [autoDismiss, setAutoDismiss] = useState(settings.auto_dismiss_seconds || 8);
  const [emailN, setEmailN] = useState(settings.notification_email ?? true);
  const [inappN, setInappN] = useState(settings.notification_inapp ?? true);
  const [teamShare, setTeamShare] = useState(settings.contribute_to_team_reports ?? true);

  const save = async () => {
    await api.put("/settings", {
      widget_position: position,
      widget_size_preference: size,
      hotkey,
      distraction_threshold_seconds: threshold,
      inactivity_threshold_seconds: inactivity,
      auto_dismiss_seconds: autoDismiss,
      notification_email: emailN,
      notification_inapp: inappN,
      contribute_to_team_reports: teamShare,
    });
    await refresh();
    toast.success("Settings saved.");
  };

  const requestDeletion = async () => {
    await api.post("/auth/request-deletion");
    toast.info("Deletion request received. We'll complete this within 24 hours.");
    await logout();
    window.location.href = "/";
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Settings</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Tune WYLO to your way of working.</h1>
      </div>

      {/* Widget */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-5">Refocus widget</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Position</Label>
            <RadioGroup value={position} onValueChange={setPosition} className="mt-3 grid grid-cols-2 gap-2">
              {["top-left", "top-right", "bottom-left", "bottom-right"].map((p) => (
                <label key={p} className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm ${position === p ? "border-[#1A3A5C] bg-[#E8ECF2]" : "border-[#E8ECF2]"}`}>
                  <RadioGroupItem value={p} />{p}
                </label>
              ))}
            </RadioGroup>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#555568] mt-6 block">Size preference</Label>
            <RadioGroup value={size} onValueChange={setSize} className="mt-3 grid grid-cols-2 gap-2">
              {["small", "medium"].map((p) => (
                <label key={p} className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm ${size === p ? "border-[#1A3A5C] bg-[#E8ECF2]" : "border-[#E8ECF2]"}`}>
                  <RadioGroupItem value={p} />{p}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">FACE Hotkey</Label>
              <select value={hotkey} onChange={(e) => setHotkey(e.target.value)} className="mt-2 w-full rounded-md border border-[#E8ECF2] bg-white px-3 py-2 text-sm">
                <option>Ctrl+Shift+Space</option>
                <option>Cmd+Shift+Space</option>
                <option>Alt+Shift+F</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Distraction threshold · {threshold}s</Label>
              <Slider value={[threshold]} min={15} max={120} step={5} onValueChange={([v]) => setThreshold(v)} className="mt-3" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Inactivity threshold · {inactivity}s</Label>
              <Slider value={[inactivity]} min={15} max={120} step={5} onValueChange={([v]) => setInactivity(v)} className="mt-3" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Auto-dismiss · {autoDismiss}s</Label>
              <Slider value={[autoDismiss]} min={3} max={20} step={1} onValueChange={([v]) => setAutoDismiss(v)} className="mt-3" />
            </div>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-between rounded-md border border-dashed border-[#E8ECF2] p-3 opacity-60 cursor-not-allowed">
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A2E]">Coming Soon: Screen Replay</p>
                      <p className="text-xs text-[#555568]">A 6-second visual replay of your last priority activity.</p>
                    </div>
                    <Switch disabled data-testid="screen-replay-toggle" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">Screen Replay will show a 6-second clip of your last activity before a FACE event. Launching soon.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-5">Notifications & privacy</h2>
        <div className="space-y-3">
          <ToggleRow label="Email notifications" desc="Weekly reports & important account events." checked={emailN} onChange={setEmailN} />
          <ToggleRow label="In-app notifications" desc="Real-time refocus and system alerts." checked={inappN} onChange={setInappN} />
          {user?.role === "s_tier" ? (
            <ToggleRow
              label="Contribute to team aggregates"
              desc="S-Tier only. For everyone else, contribution is required to make workspace analytics meaningful."
              checked={teamShare}
              onChange={setTeamShare}
            />
          ) : (
            <div className="flex items-start justify-between gap-3 rounded-md border border-[#E8ECF2] p-3 bg-[#F8F9FC] opacity-80">
              <div>
                <p className="text-sm font-semibold text-[#1A1A2E]">Contribute to team aggregates</p>
                <p className="text-xs text-[#555568]">
                  Required. Your data is always aggregated and anonymised before it ever leaves your workspace - no individual is ever identified.
                </p>
              </div>
              <span className="text-xs uppercase tracking-[0.18em] text-[#1D9E75] font-semibold">Required</span>
            </div>
          )}
        </div>
      </section>

      <div className="flex gap-3">
        <Button onClick={save} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="settings-save">
          Save settings
        </Button>
      </div>

      {/* Sign-in methods (Google linking + password) */}
      <SignInMethods />

      {/* Domain verification (visible to workspace admins on personal-email domains) */}
      <DomainVerification />

      {/* Delete account */}
      <section className="rounded-xl border border-[#E24B4A]/30 bg-[#E24B4A]/5 p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-3">Delete my account</h2>
        <p className="text-sm text-[#555568] mb-4">
          You can permanently delete your WYLO account and request deletion of all associated data. We'll complete this within 24 hours via our S-Tier team.
        </p>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-[#E24B4A] text-[#E24B4A] hover:bg-[#E24B4A]/10" data-testid="settings-delete-btn">
              Request deletion
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Permanently delete your account?</DialogTitle>
              <DialogDescription>
                This will queue a deletion request fulfilled within 24 hours. All FACE history, hierarchy settings, and personal data will be removed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline">Cancel</Button>
              <Button onClick={requestDeletion} className="bg-[#E24B4A] hover:bg-[#c03d3c] text-white" data-testid="settings-delete-confirm">
                Yes, delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-[#E8ECF2] p-3 bg-[#F8F9FC]">
      <div>
        <p className="text-sm font-semibold text-[#1A1A2E]">{label}</p>
        <p className="text-xs text-[#555568]">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
